
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Restaurant } from '../../../data/models';
import { FallbackImage } from '../../../shared/fallback-image';

@Component({
  selector: 'app-restaurant-header',
  imports: [FallbackImage, TranslatePipe],
  template: `
    <header class="qm-header relative border-b border-slate-100 bg-white px-6 pt-8 pb-5">
      @if (showInfo()) {
        <button
          #infoBtn
          type="button"
          class="qm-info-btn absolute top-4 right-4 grid h-10 w-10 cursor-pointer place-items-center rounded-full shadow-md transition-transform active:scale-95"
          style="background: var(--qm-accent); color: var(--qm-accent-fg)"
          [attr.aria-label]="'header.info' | translate"
          (click)="openInfo.emit(infoBtn)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        </button>
      }

      <div class="mx-auto flex max-w-md flex-col items-center gap-3 text-center">
        @if (restaurant().logoUrl; as logo) {
          <img
            [src]="logo"
            [alt]="restaurant().name"
            width="144"
            height="144"
            loading="eager"
            decoding="async"
            appFallbackImage
            class="qm-logo h-auto w-36"
          />
        }
        <h1 class="qm-title text-3xl font-bold tracking-tight text-slate-900">
          {{ restaurant().name }}
        </h1>
        @if (restaurant().tagline) {
          <p class="qm-tagline text-xs font-medium tracking-widest text-slate-500 uppercase">
            {{ restaurant().tagline }}
          </p>
        }
        @if (tableName(); as name) {
          <span
            animate.enter="chip-enter"
            animate.leave="chip-leave"
            class="qm-table-chip inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold"
            style="background: var(--qm-accent-tint); color: var(--qm-accent-ink)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M12 21s-6-5.686-6-10a6 6 0 0 1 12 0c0 4.314-6 10-6 10Z"></path>
              <circle cx="12" cy="11" r="2"></circle>
            </svg>
            <span class="sr-only">{{ 'header.yourTable' | translate }} </span>{{ name }}
          </span>
        }
        <div
          class="qm-accent h-1 w-16 rounded-full"
          style="background: var(--qm-rule)"
          aria-hidden="true"
        ></div>
      </div>
    </header>
  `,
  styles: `
    :host {
      display: block;
    }

    @keyframes chip-enter {
      from {
        opacity: 0;
        transform: translateY(6px) scale(0.9);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    @keyframes chip-leave {
      from {
        opacity: 1;
        transform: scale(1);
      }
      to {
        opacity: 0;
        transform: scale(0.9);
      }
    }

    .chip-enter {
      animation: chip-enter 300ms cubic-bezier(0.22, 1, 0.36, 1);
    }

    .chip-leave {
      animation: chip-leave 180ms ease-in forwards;
    }

    @media (prefers-reduced-motion: reduce) {
      .chip-enter,
      .chip-leave {
        animation-duration: 1ms;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RestaurantHeader {
  restaurant = input.required<Restaurant>();
  showInfo = input(false);
  /** Table display name (e.g. «Стол 7»); shows a chip so the guest recognizes their table. */
  tableName = input<string | null>(null);
  /** Emits the trigger button so the sheet can restore focus to it on close. */
  openInfo = output<HTMLElement>();
}
